import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = unknown, false = anon
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (err) {
        // Not-logged-in path is expected; only log unexpected errors
        if (err?.response?.status && err.response.status !== 401) {
          console.warn("Auth bootstrap failed:", err?.message || err);
        }
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const googleLogin = useCallback(async (session_id) => {
    const { data } = await api.post("/auth/google/callback", { session_id });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("Logout call failed:", err?.message || err); }
    setUser(false);
  }, []);

  const deleteAccount = useCallback(async (payload) => {
    await api.delete("/auth/me", { data: payload || {} });
    setUser(false);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("Session refresh failed:", err?.message || err);
      }
      setUser(false);
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, googleLogin, logout, deleteAccount, refresh }),
    [user, loading, login, googleLogin, logout, deleteAccount, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
